import React, { useEffect, useRef } from 'react';
import { useI18n } from '../../i18n/index.jsx';

// Six single-digit boxes for an emailed code: numeric keyboard on phones,
// auto-advance, Backspace goes back, arrows move, pasting the whole code
// (or iOS/Android "From Messages / Mail" autofill) fills every box.
// onComplete fires once all six digits are present.
export default function CodeInput({ value, onChange, onComplete, disabled, autoFocus = true }) {
  const { t } = useI18n();
  const boxes = useRef([]);
  const digits = Array.from({ length: 6 }, (_, i) => value[i] || '');

  useEffect(() => {
    if (autoFocus) boxes.current[0]?.focus();
  }, [autoFocus]);

  const setAll = (next) => {
    const clean = next.replace(/\D/g, '').slice(0, 6);
    onChange(clean);
    if (clean.length === 6) onComplete?.(clean);
    return clean;
  };

  const handleInput = (i, e) => {
    const typed = e.target.value.replace(/\D/g, '');
    if (!typed) return;
    if (typed.length > 1) { // paste or autofill into one box
      const filled = setAll(typed);
      boxes.current[Math.min(filled.length, 5)]?.focus();
      return;
    }
    const next = (value.slice(0, i) + typed + value.slice(i + 1)).slice(0, 6);
    setAll(next);
    if (i < 5) boxes.current[i + 1]?.focus();
  };

  const handleKey = (i, e) => {
    if (e.key === 'Backspace') {
      e.preventDefault();
      if (digits[i]) setAll(value.slice(0, i) + value.slice(i + 1));
      else if (i > 0) { setAll(value.slice(0, i - 1) + value.slice(i)); boxes.current[i - 1]?.focus(); }
    } else if (e.key === 'ArrowLeft' && i > 0) boxes.current[i - 1]?.focus();
    else if (e.key === 'ArrowRight' && i < 5) boxes.current[i + 1]?.focus();
  };

  const handlePaste = (e) => {
    const text = e.clipboardData.getData('text');
    if (!/\d/.test(text)) return;
    e.preventDefault();
    const filled = setAll(text);
    boxes.current[Math.min(filled.length, 5)]?.focus();
  };

  return (
    <div className="code-input" role="group" aria-label={t('auth.codeLabel')} onPaste={handlePaste}>
      {digits.map((d, i) => (
        <input
          key={i}
          ref={(el) => { boxes.current[i] = el; }}
          value={d}
          onChange={(e) => handleInput(i, e)}
          onKeyDown={(e) => handleKey(i, e)}
          onFocus={(e) => e.target.select()}
          inputMode="numeric"
          pattern="[0-9]*"
          autoComplete={i === 0 ? 'one-time-code' : 'off'}
          maxLength={i === 0 ? 6 : 1}
          disabled={disabled}
          aria-label={t('auth.codeDigit', { n: i + 1 })}
        />
      ))}
    </div>
  );
}
